import { Color, Mesh, type Material, type Object3D } from 'three';

/** Materials the standard model pipeline special-cases; tinting them is invisible or wrong. */
const SKIP_MATERIAL_NAMES = new Set<string>(['Image-arch']);

type EmissiveMaterial = Material & { emissive: Color; emissiveIntensity: number };

const hasEmissive = (material: Material | null | undefined): material is EmissiveMaterial =>
	Boolean(material) && 'emissive' in (material as Material) && 'emissiveIntensity' in (material as Material);

/** Original materials of every mesh under a tinted object, so the tint can be undone. */
export interface EmissiveTintBackup {
	entries: Array<{ mesh: Mesh; original: Material | Array<Material>; clones: Array<EmissiveMaterial> }>;
}

/**
 * Emissive tint on one object. Every material is CLONED first: the model manager returns shallow
 * clones that share materials across instances of the same SKU, so mutating in place would tint
 * every other copy of the model. Used for the drag lift and for the hover glow.
 */
export const applyEmissiveTint = (object: Object3D, color: number, intensity: number): EmissiveTintBackup => {
	const backup: EmissiveTintBackup = { entries: [] };
	const tintColor = new Color(color);

	object.traverse((child) => {
		if (!(child instanceof Mesh) || !child.material) return;
		const original = child.material as Material | Array<Material>;
		const clones: Array<EmissiveMaterial> = [];
		const tint = (material: Material): Material => {
			if (!hasEmissive(material) || SKIP_MATERIAL_NAMES.has(material.name)) return material;
			const cloned = material.clone() as EmissiveMaterial;
			cloned.emissive = tintColor.clone();
			cloned.emissiveIntensity = intensity;
			cloned.needsUpdate = true;
			clones.push(cloned);
			return cloned;
		};
		child.material = Array.isArray(original) ? original.map(tint) : tint(original);
		backup.entries.push({ mesh: child, original, clones });
	});

	return backup;
};

export const setEmissiveTintIntensity = (backup: EmissiveTintBackup, intensity: number): void => {
	for (const { clones } of backup.entries) {
		for (const clone of clones) clone.emissiveIntensity = intensity;
	}
};

export const restoreEmissiveTint = (backup: EmissiveTintBackup): void => {
	for (const { mesh, original, clones } of backup.entries) {
		mesh.material = original;
		for (const clone of clones) clone.dispose();
	}
	backup.entries.length = 0;
};
